import React, { useMemo, useRef, useEffect, useState } from "react";
import {
  Box,
  Grid,
  TextField,
  Typography,
  Table,
  TableRow,
  TableHead,
  TableBody,
  TableCell,
  Chip,
  Collapse,
  Skeleton,
  Pagination,
  IconButton,
} from "@mui/material";
import { DateRangePicker } from "@mui/lab";
import { DateRangeOutlined, OpenInNew, KeyboardArrowDown, KeyboardArrowUp } from "@mui/icons-material";
import { makeStyles } from "@mui/styles";
import clsx from "clsx";
import { format } from "date-fns";
import { Link } from "react-router-dom";

const getCoefficientColor = (value) => {
  const num = parseFloat(value) || 0;
  if (num < 0.8) return { backgroundColor: "#FF3C4380", color: "#fff" };
  if (num > 1.3) return { backgroundColor: "#FF3C4380", color: "#fff" };
  return { backgroundColor: "#f5f5f5", color: "#000" };
};

const getCoefficient = (row) => {
  if (row.coefficient != null && row.coefficient !== "") return row.coefficient;
  const e = parseFloat(row.estimate);
  const a = parseFloat(row.actual_time);
  if (Number.isFinite(e) && Number.isFinite(a) && a !== 0) return (e / a).toFixed(2);
  return "—";
};

const getGroupLabel = (group) => {
  const label = group.brand || group.model || group.year || group.engine;
  return label || "Не определено";
};

const GroupedView = ({ groups, analysisType, classes }) => {
  const [expanded, setExpanded] = useState({});
  const toggle = (index) => setExpanded((prev) => ({ ...prev, [index]: !prev[index] }));
  const countKey = analysisType === "less_than_08" ? "less_than_08_count" : "more_than_13_count";

  return (
    <Box sx={{ marginTop: "16px" }}>
      {groups.map((group, gi) => {
        const groupLabel = getGroupLabel(group);
        const isOpen = !!expanded[gi];
        return (
          <Box key={`group-${gi}`} sx={{ marginBottom: "8px" }}>
            <Box
              className={classes.groupHeaderRow}
              onClick={() => toggle(gi)}
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                cursor: "pointer",
                padding: "10px 12px",
                backgroundColor: "#F4F6F8",
                borderBottom: "2px solid #E0E0E0",
                borderRadius: isOpen ? "8px 8px 0 0" : "8px",
                "&:hover": { backgroundColor: "#EBEEF2" },
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <IconButton size="small" sx={{ padding: 0 }}>
                  {isOpen ? <KeyboardArrowUp fontSize="small" /> : <KeyboardArrowDown fontSize="small" />}
                </IconButton>
                <Typography sx={{ fontWeight: 700, fontSize: 14, color: "#282828" }}>
                  {groupLabel}
                </Typography>
              </Box>
              <Chip size="small" label={`${group[countKey] ?? 0} шт.`} variant="outlined" />
            </Box>
            <Collapse in={isOpen} timeout={250} unmountOnExit>
              <Box sx={{ paddingLeft: "20px" }}>
                <Table size="small" className={classes.table} sx={{ marginTop: 0 }}>
                  <TableHead>
                    <TableRow className={classes.tableHeadRow}>
                      <TableCell>Название работы</TableCell>
                      <TableCell align="center">№ заявки</TableCell>
                      <TableCell align="center">Норматив, ч</TableCell>
                      <TableCell align="center">Факт, ч</TableCell>
                      <TableCell align="center">Коэффициент</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(group.items || []).map((row, index) => {
                      const coefficient = getCoefficient(row);
                      return (
                        <TableRow key={`${gi}-${row.order_id}-${index}`} className={classes.tableBodyRow}>
                          <TableCell>{row.work_name || "—"}</TableCell>
                          <TableCell align="center">
                            {row.order_id != null ? (
                              <Link
                                to={`/applications/${row.order_id}`}
                                className={classes.orderLink}
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                {row.order_id}
                                <OpenInNew className={classes.orderLinkIcon} />
                              </Link>
                            ) : "—"}
                          </TableCell>
                          <TableCell align="center">{row.estimate ?? "—"}</TableCell>
                          <TableCell align="center">{row.actual_time ?? "—"}</TableCell>
                          <TableCell align="center">
                            <Chip
                              size="small"
                              label={coefficient}
                              className={classes.tableBodyChip}
                              sx={getCoefficientColor(coefficient)}
                            />
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </Box>
            </Collapse>
          </Box>
        );
      })}
      {groups.length === 0 && (
        <Typography align="center" color="textSecondary" py={3} fontSize={14}>
          Нет данных
        </Typography>
      )}
    </Box>
  );
};

const SEARCH_DEBOUNCE_MS = 1500;

const groupButtons = [
  { key: "group_by_brand", label: "По марке" },
  { key: "group_by_model", label: "По модели" },
  { key: "group_by_year", label: "По году" },
  { key: "group_by_engine", label: "По двигателю" },
];

const FilterWorkForAnalysis = ({
  filter = {},
  data = {},
  totalPages = 1,
  onChangeFilter,
  onApply,
  onTabChange,
  onGroupToggle,
  onPageChange,
  isLoading = false,
}) => {
  const classes = useStyles();
  const searchDebounceRef = useRef(null);

  const analysisType = filter.analysisType || "less_than_08";
  const hasGroups = Array.isArray(data?.groups) && data.groups.length > 0;
  const groups = data?.groups ?? [];
  const rows = hasGroups ? [] : (data?.analysis?.[analysisType] ?? []);

  const dateRange = useMemo(
    () => [
      filter.date_from ? new Date(filter.date_from) : null,
      filter.date_to ? new Date(filter.date_to) : null,
    ],
    [filter.date_from, filter.date_to]
  );

  useEffect(() => {
    return () => {
      if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    };
  }, []);

  const handleDateChange = (newValue) => {
    const [start, end] = newValue || [null, null];
    const nextFilter = {
      ...filter,
      date_from: start ? format(start, "yyyy-MM-dd") : "",
      date_to: end ? format(end, "yyyy-MM-dd") : "",
      work_name: "", // при смене периода очищаем поиск
    };
    onChangeFilter?.(nextFilter);
    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
      searchDebounceRef.current = null;
    }
    onApply?.();
  };

  const handleWorkNameChange = (e) => {
    const value = e.target.value;
    onChangeFilter?.({ ...filter, work_name: value });
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => {
      searchDebounceRef.current = null;
      onApply?.();
    }, SEARCH_DEBOUNCE_MS);
  };

  const handleWorkNameBlur = () => {
    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
      searchDebounceRef.current = null;
      onApply?.();
    }
  };

  return (
    <Box className={classes.root}>
      <Grid container spacing={5} alignItems="center">
        <Grid item xs={4}>
          <div className={classes.rowFormItem}>
            <Typography className={classes.label}>Период</Typography>
            <DateRangePicker
              value={dateRange}
              style={{ width: "100%" }}
              onChange={handleDateChange}
              renderInput={(startProps) => {
                const { value: _inputValue, ...restInputProps } = startProps.inputProps || {};
                const periodDisplay =
                  dateRange[0] && dateRange[1]
                    ? `${format(dateRange[0], "dd.MM.yyyy")} - ${format(dateRange[1], "dd.MM.yyyy")}`
                    : dateRange[0]
                    ? format(dateRange[0], "dd.MM.yyyy")
                    : "";
                return (
                  <label style={{ width: "100%" }}>
                    <Grid container spacing={1} alignItems="center">
                      <Grid item sx={{ flex: 1 }}>
                        <TextField
                          fullWidth
                          placeholder="01.01.2025 - 31.12.2025"
                          className={classes.input}
                          value={periodDisplay}
                          inputProps={{ ...restInputProps, readOnly: true }}
                        />
                      </Grid>
                      <Grid item>
                        <DateRangeOutlined className={classes.calendarIcon} />
                      </Grid>
                    </Grid>
                  </label>
                );
              }}
            />
          </div>
        </Grid>
        <Grid item xs={8}>
          <div className={clsx(classes.rowFormItem, classes.searchRowNoWrap)}>
            <Typography className={clsx(classes.label, classes.labelNoWrap)}>Поиск по работам</Typography>
            <TextField
              size="small"
              fullWidth
              placeholder="Название работы"
              className={classes.input}
              value={filter.work_name ?? ""}
              onChange={handleWorkNameChange}
              onBlur={handleWorkNameBlur}
            />
          </div>
        </Grid>
      </Grid>

      <Box className={classes.tabs}>
        <Typography
          component="span"
          className={clsx(classes.tab, analysisType === "less_than_08" && classes.tabActive)}
          onClick={() => onTabChange?.("less_than_08")}
        >
          Коэффициент &lt;0,8
        </Typography>
        <Typography
          component="span"
          className={clsx(classes.tab, analysisType === "more_than_13" && classes.tabActive)}
          onClick={() => onTabChange?.("more_than_13")}
        >
          Коэффициент &gt;1,3
        </Typography>
      </Box>

      <Box className={classes.groupButtons}>
        <Typography className={classes.groupLabel}>Группировка:</Typography>
        {groupButtons.map((btn) => (
          <Chip
            key={btn.key}
            label={btn.label}
            size="small"
            variant={filter[btn.key] ? "filled" : "outlined"}
            color={filter[btn.key] ? "primary" : "default"}
            onClick={() => onGroupToggle?.(btn.key)}
            className={classes.groupChip}
          />
        ))}
      </Box>

      {!isLoading && hasGroups && (
        <GroupedView
          groups={groups}
          analysisType={analysisType}
          classes={classes}
        />
      )}

      <Table className={classes.table} style={hasGroups && !isLoading ? { display: "none" } : {}}>
        <TableHead>
          <TableRow className={classes.tableHeadRow}>
            <TableCell>Название работы</TableCell>
            <TableCell align="center">№ заявки</TableCell>
            <TableCell align="center">Норматив, ч</TableCell>
            <TableCell align="center">Факт, ч</TableCell>
            <TableCell align="center">Коэффициент</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {!isLoading && !hasGroups &&
            rows.map((row, index) => {
              const coefficient = getCoefficient(row);
              return (
                <TableRow key={`${row.order_id}-${row.work_name}-${index}`} className={classes.tableBodyRow}>
                  <TableCell>{row.work_name || "—"}</TableCell>
                  <TableCell align="center">
                    {row.order_id != null ? (
                      <Link
                        to={`/applications/${row.order_id}`}
                        className={classes.orderLink}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {row.order_id}
                        <OpenInNew className={classes.orderLinkIcon} />
                      </Link>
                    ) : "—"}
                  </TableCell>
                  <TableCell align="center">{row.estimate ?? "—"}</TableCell>
                  <TableCell align="center">{row.actual_time ?? "—"}</TableCell>
                  <TableCell align="center">
                    <Chip
                      size="small"
                      label={coefficient}
                      className={classes.tableBodyChip}
                      sx={getCoefficientColor(coefficient)}
                    />
                  </TableCell>
                </TableRow>
              );
            })}
          {!isLoading && !hasGroups && rows.length === 0 && (
            <TableRow>
              <TableCell colSpan={5} align="center" className={classes.emptyCell}>
                Нет данных
              </TableCell>
            </TableRow>
          )}
          {isLoading && (
            <>
              {Array.from({ length: 8 }).map((_, i) => (
                <TableRow key={i}>
                  <TableCell><Skeleton /></TableCell>
                  <TableCell><Skeleton /></TableCell>
                  <TableCell><Skeleton /></TableCell>
                  <TableCell><Skeleton /></TableCell>
                  <TableCell><Skeleton /></TableCell>
                </TableRow>
              ))}
            </>
          )}
        </TableBody>
      </Table>
      {totalPages > 1 && (
        <Box className={classes.paginationWrap}>
          <Pagination
            page={Number(filter.page) || 1}
            count={totalPages}
            onChange={onPageChange}
            color="primary"
          />
        </Box>
      )}
    </Box>
  );
};

const useStyles = makeStyles(() => ({
  root: {
    gap: 16,
    display: "flex",
    marginTop: "24px",
    flexDirection: "column",
  },
  rowFormItem: {
    gap: "6px",
    display: "flex",
    alignItems: "center",

    "& > div:not(.MuiFormControl-root)": {
      width: "100%",
    }
  },
  searchRowNoWrap: {
    flexWrap: "nowrap",
    minWidth: 0,
  },
  labelNoWrap: {
    whiteSpace: "nowrap",
    flexShrink: 0,
  },
  label: {
    fontSize: 14,
    lineHeight: 1.2,
    color: "#434146",
  },
  input: {
    "& .MuiOutlinedInput-root": {
      backgroundColor: "#fff",
      borderColor: "#E0E0E0",
      borderRadius: "6px",
    },
    "& .MuiOutlinedInput-input": {
      height: 40,
      boxSizing: "border-box",
      fontSize: 16,
    },
    "& .MuiOutlinedInput-notchedOutline": {
      borderColor: "#E0E0E0",
    },
  },
  calendarIcon: {
    fontSize: 22,
    display: "flex",
    color: "#9E9E9E",
    cursor: "pointer",
    alignItems: "center",
    justifyContent: "center",

    "&:hover": {
      color: "#FF3C43"
    }
  },
  tabs: {
    display: "flex",
    gap: 24,
    alignItems: "center",
    borderBottom: "2px solid #E0E0E0",
    paddingBottom: 8,
  },
  tab: {
    fontSize: 14,
    fontWeight: 400,
    color: "#434146",
    cursor: "pointer",
    position: "relative",

    "&:after": {
      height: "2px",
      width: "34px",
      position: "absolute",
      left: "0", bottom: "-10px",
      backgroundColor: "#FF3C43",
    },
  },
  tabActive: {
    fontWeight: 700,
    color: "#434146",
    "&:after": {
      content: "''"
    }
  },
  groupButtons: {
    display: "flex",
    gap: 8,
    alignItems: "center",
    flexWrap: "wrap",
  },
  groupLabel: {
    fontSize: 13,
    color: "#8698B1",
    marginRight: 4,
  },
  groupChip: {
    cursor: "pointer",
    fontSize: 13,
  },
  table: {
    marginTop: 16,
  },
  paginationWrap: {
    marginTop: 16,
    display: "flex",
    justifyContent: "flex-end",
  },
  tableHeadRow: {
    "&.MuiTableRow-head .MuiTableCell-root": {
      borderTop: "1px solid #F3F3F3",
      borderBottom: "2px solid #C4C4C4",
      borderLeft: "none",
      color: "#434146",
      fontWeight: 400,
      textAlign: "center",
    },
  },
  tableBodyRow: {
    "&.MuiTableRow-root .MuiTableCell-root": {
      borderBottom: "1px solid #F3F3F3",
      borderLeft: "none",
      fontSize: 16,
      color: "#434146",
      fontWeight: 400,
    },
  },
  tableBodyChip: {
    width: 90,
    height: 28,
  },
  emptyCell: {
    padding: 24,
    color: "#818181",
    fontSize: 14,
  },
  orderLink: {
    display: "inline-flex",
    alignItems: "center",
    gap: 4,
    color: "#FF3C43",
    textDecoration: "none",
    "&:hover": {
      textDecoration: "underline",
    },
  },
  orderLinkIcon: {
    fontSize: 16,
  },
}));

export default FilterWorkForAnalysis;
